import React, { useState, useEffect } from "react";
import { Warehouse } from "../../types/Warehouse";
import { useFetchContext } from "../../context/FetchContext";
import { useToast } from "../../context/ToastContext";
import { IDataPagination } from "types/User";
import Select from "components/ui/Select";
import Table from "components/ui/Table";
import Pagination from "components/ui/Pagination";

interface Product {
  quantity: number;
  productCode: string;
  pendingOrders: number;
}

interface InventoryResponse {
  total: number;
  totalPages: number;
  items: Product[];
  info: {
    totalStocks: number;
  };
}

const ProductsList: React.FC = () => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>("");
  const [products, setProducts] = useState<Product[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalStocks, setTotalStocks] = useState<number>(0);
  const [searchKeyword, setSearchKeyword] = useState<string>("");
  const [orderBy, setOrderBy] = useState<string>("quantity");
  const [orderType, setOrderType] = useState<string>("DESC");
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const {
    fetchData,
    state: { isLoading },
  } = useFetchContext();
  const { showToast } = useToast();

  useEffect(() => {
    const getWarehouses = async () => {
      try {
        const response = await fetchData<IDataPagination<Warehouse>>(
          "/warehouses"
        );
        if (response && response.data && response.data.items) {
          setWarehouses(response.data.items);
        }
      } catch (error) {
        console.error("Error fetching warehouses:", error);
        showToast("Errore durante il caricamento dei magazzini", "error");
      }
    };

    getWarehouses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedWarehouse) {
      fetchProducts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWarehouse, currentPage, orderBy, orderType]);

  const fetchProducts = async () => {
    try {
      const response = await fetchData<InventoryResponse>(
        `/products/inventory-check?warehouseId=${selectedWarehouse}&page=${currentPage}&searchKeyword=${searchKeyword}&orderType=${orderType}&orderBy=${orderBy}`
      );

      if (response && response.data) {
        setProducts(response.data.items);
        setTotalPages(response.data.totalPages);
        setTotalItems(response.data.total);
        setTotalStocks(response.data.info.totalStocks);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      showToast("Errore durante il caricamento dei prodotti", "error");
    }
  };

  const handleWarehouseChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedWarehouse(e.target.value);
    setCurrentPage(1);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchKeyword(value);

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchProducts();
    }, 500);

    setSearchTimeout(timeout);
  };

  const handleSort = (column: string) => {
    if (orderBy === column) {
      setOrderType(orderType === "ASC" ? "DESC" : "ASC");
    } else {
      setOrderBy(column);
      setOrderType("DESC");
    }
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  return (
    <div className="overflow-hidden bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h2 className="text-lg font-medium text-gray-900">Lista Prodotti</h2>

        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label
              htmlFor="warehouse"
              className="block text-sm font-medium text-gray-700"
            >
              Seleziona Magazzino
            </label>
            <Select
              id="warehouse"
              value={selectedWarehouse}
              onChange={handleWarehouseChange}
              className="mt-1"
              fullWidth
            >
              <option value="">Seleziona un magazzino</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label
              htmlFor="search"
              className="block text-sm font-medium text-gray-700"
            >
              Ricerca
            </label>
            <input
              type="text"
              id="search"
              value={searchKeyword}
              onChange={handleSearch}
              placeholder="Cerca per codice prodotto"
              className="mt-1 block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-200">
        {isLoading ? (
          <div className="text-center py-4">
            <svg
              className="animate-spin h-8 w-8 mx-auto text-blue-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="mt-2 text-gray-500">Caricamento dati...</p>
          </div>
        ) : !selectedWarehouse ? (
          <div className="text-center py-4">
            <p className="text-gray-500">
              Seleziona un magazzino per visualizzare i dati
            </p>
          </div>
        ) : (
          <>
            <Table
              data={products}
              columns={[
                {
                  key: "productCode",
                  header: "Codice Prodotto",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm font-medium text-gray-900">
                      {item.productCode}
                    </div>
                  ),
                },
                {
                  key: "quantity",
                  header: "Quantità Disponibile",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-500">{item.quantity}</div>
                  ),
                },
                {
                  key: "pendingOrders",
                  header: "Ordini in Sospeso",
                  sortable: true,
                  render: (item) => (
                    <div className="text-sm text-gray-500">
                      {item.pendingOrders}
                    </div>
                  ),
                },
              ]}
              keyExtractor={(item) => item.productCode}
              isLoading={isLoading}
              emptyMessage="Nessun prodotto trovato"
              sortColumn={orderBy}
              sortDirection={orderType as "ASC" | "DESC"}
              onSort={handleSort}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              pageSize={products.length || 10}
              onPageChange={handlePageChange}
              summaryText={`Showing ${
                (currentPage - 1) * (products.length || 10) + 1
              } to ${Math.min(
                currentPage * (products.length || 10),
                totalItems
              )} of ${totalItems} products (Total stock: ${totalStocks})`}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ProductsList;
